import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Syncs document-level metadata that's independent of which page is mounted
 * (<html lang>, og:locale) whenever the i18next language changes.
 *
 * Title/description/og:title/og:description are deliberately NOT set here —
 * every page already sets its own via useDocumentMetadata. This hook runs on
 * AppContent, an ancestor of every routed page, so on the first render its
 * effect fires AFTER the page's (React flushes child effects before parent
 * effects on mount) — setting generic copy here would silently overwrite the
 * per-page title/description on every direct/deep link and crawler visit.
 */
export const useDocumentMeta = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const lang = i18n.language;

    document.documentElement.lang = lang;

    let ogLocale = document.querySelector<HTMLMetaElement>('meta[property="og:locale"]');
    if (!ogLocale) {
      ogLocale = document.createElement('meta');
      ogLocale.setAttribute('property', 'og:locale');
      document.head.appendChild(ogLocale);
    }
    ogLocale.setAttribute('content', t('meta.og_locale'));

    let ogLocaleAlt = document.querySelector<HTMLMetaElement>('meta[property="og:locale:alternate"]');
    if (!ogLocaleAlt) {
      ogLocaleAlt = document.createElement('meta');
      ogLocaleAlt.setAttribute('property', 'og:locale:alternate');
      document.head.appendChild(ogLocaleAlt);
    }
    ogLocaleAlt.setAttribute('content', lang === 'en' ? 'it_IT' : 'en_US');
  }, [i18n.language, t]);
};
